/**
 * Notifications API — Supabase
 */

import { supabase, TABLES, fromDbList } from './db';
import { Notification, NotificationType } from '../types';
import { isNotificationTypeEnabled } from './userSettings';

// ============================================================================
// CREATE
// ============================================================================

export async function createNotification(
  userId: string,
  type: NotificationType,
  title: string,
  message: string,
  options?: {
    actionUrl?: string;
    actionLabel?: string;
    relatedTaskId?: string;
    relatedConnectionId?: string;
    relatedUserId?: string;
    icon?: string;
    priority?: 'low' | 'normal' | 'high';
  }
): Promise<string | null> {
  try {
    const enabled = await isNotificationTypeEnabled(userId, type);
    if (!enabled) return null;

    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from(TABLES.NOTIFICATIONS)
      .insert({
        user_id: userId,
        type,
        title,
        message,
        read: false,
        action_url: options?.actionUrl ?? null,
        action_label: options?.actionLabel ?? null,
        related_task_id: options?.relatedTaskId ?? null,
        related_connection_id: options?.relatedConnectionId ?? null,
        related_user_id: options?.relatedUserId ?? null,
        icon: options?.icon ?? null,
        priority: options?.priority ?? 'normal',
        created_at: now,
        updated_at: now,
      })
      .select('id')
      .single();

    if (error) throw error;
    return (data as { id: string }).id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
}

export async function notifyConnectionRequest(
  taskOwnerId: string, taskerId: string, taskId: string,
  taskTitle: string, taskerName: string, connectionRequestId: string
): Promise<string | null> {
  return createNotification(taskOwnerId, 'connection_request', 'New Connection Request',
    `${taskerName} wants to connect about "${taskTitle}"`,
    { actionUrl: '/task-dashboard?tab=connections', actionLabel: 'View Request',
      relatedTaskId: taskId, relatedConnectionId: connectionRequestId,
      relatedUserId: taskerId, icon: 'user-plus', priority: 'high' });
}

export async function notifyConnectionAccepted(
  taskerId: string, taskOwnerId: string, taskId: string,
  taskTitle: string, ownerName: string, connectionRequestId: string
): Promise<string | null> {
  return createNotification(taskerId, 'connection_accepted', 'Connection Request Accepted',
    `${ownerName} accepted your connection request for "${taskTitle}"`,
    { actionUrl: `/tasks/${taskId}`, actionLabel: 'Open task & chat',
      relatedTaskId: taskId, relatedConnectionId: connectionRequestId,
      relatedUserId: taskOwnerId, icon: 'check-circle', priority: 'normal' });
}

export async function notifyOwnerConnectionAccepted(
  taskOwnerId: string, taskerId: string, taskId: string,
  taskTitle: string, taskerName: string, connectionRequestId: string
): Promise<string | null> {
  return createNotification(taskOwnerId, 'connection_accepted', 'Connection confirmed',
    `You accepted ${taskerName}'s request to collaborate on "${taskTitle}". Open the task to chat.`,
    { actionUrl: `/tasks/${taskId}`, actionLabel: 'Open task',
      relatedTaskId: taskId, relatedConnectionId: connectionRequestId,
      relatedUserId: taskerId, icon: 'check-circle', priority: 'normal' });
}

export async function notifyConnectionRejected(
  taskerId: string, taskOwnerId: string, taskId: string,
  taskTitle: string, ownerName: string
): Promise<string | null> {
  return createNotification(taskerId, 'connection_rejected', 'Connection Request Declined',
    `${ownerName} declined your connection request for "${taskTitle}"`,
    { actionUrl: '/marketplace', actionLabel: 'Browse Tasks',
      relatedTaskId: taskId, relatedUserId: taskOwnerId, icon: 'x-circle', priority: 'low' });
}

export async function notifyNewMessage(
  recipientId: string, senderId: string, senderName: string,
  taskId: string, taskTitle: string, messagePreview: string, connectionId: string
): Promise<string | null> {
  const preview = messagePreview.length > 100 ? messagePreview.substring(0, 100) + '...' : messagePreview;
  return createNotification(recipientId, 'new_message', `New message from ${senderName}`, preview,
    { actionUrl: `/task-dashboard?tab=connections&chat=${connectionId}`, actionLabel: 'Open Chat',
      relatedTaskId: taskId, relatedConnectionId: connectionId,
      relatedUserId: senderId, icon: 'message-circle', priority: 'high' });
}

// ============================================================================
// GET
// ============================================================================

export async function getUserNotifications(
  userId: string,
  options?: { limit?: number; unreadOnly?: boolean }
): Promise<Notification[]> {
  let q = supabase
    .from(TABLES.NOTIFICATIONS)
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (options?.unreadOnly) q = q.eq('read', false);
  if (options?.limit) q = q.limit(options.limit);

  const { data, error } = await q;
  if (error) throw error;
  return fromDbList<Notification>(data ?? []);
}

export async function getUnreadNotificationCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from(TABLES.NOTIFICATIONS)
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('read', false);
  if (error) throw error;
  return count ?? 0;
}

export async function getNotification(notificationId: string): Promise<Notification | null> {
  const { data, error } = await supabase
    .from(TABLES.NOTIFICATIONS)
    .select('*')
    .eq('id', notificationId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const { fromDb } = await import('./db');
  return fromDb<Notification>(data);
}

// ============================================================================
// REALTIME SUBSCRIPTION
// ============================================================================

export function subscribeToUserNotifications(
  userId: string,
  callback: (notifications: Notification[]) => void
): () => void {
  const load = async () => {
    const notifs = await getUserNotifications(userId);
    callback(notifs);
  };

  void load();

  const channel = supabase
    .channel(`notifications-${userId}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.NOTIFICATIONS, filter: `user_id=eq.${userId}` }, () => void load())
    .subscribe();

  return () => { void supabase.removeChannel(channel); };
}

// ============================================================================
// UPDATE
// ============================================================================

export async function markNotificationAsRead(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from(TABLES.NOTIFICATIONS)
    .update({ read: true, read_at: new Date().toISOString() })
    .eq('id', notificationId);
  if (error) throw error;
}

export async function markAllNotificationsAsRead(userId: string): Promise<void> {
  const { error } = await supabase
    .from(TABLES.NOTIFICATIONS)
    .update({ read: true, read_at: new Date().toISOString() })
    .eq('user_id', userId)
    .eq('read', false);
  if (error) throw error;
}

// ============================================================================
// DELETE
// ============================================================================

export async function deleteNotification(notificationId: string): Promise<void> {
  const { error } = await supabase.from(TABLES.NOTIFICATIONS).delete().eq('id', notificationId);
  if (error) throw error;
}

export async function deleteAllReadNotifications(userId: string): Promise<void> {
  const { error } = await supabase
    .from(TABLES.NOTIFICATIONS)
    .delete()
    .eq('user_id', userId)
    .eq('read', true);
  if (error) throw error;
}
